import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { UserPlus, Send, Copy, CheckCircle2, Users, Mail, X, AlertTriangle, Link2, Share2 } from 'lucide-react';
import PipelineTabs, { PIPELINE_TABS } from '@/components/PipelineTabs';

export default function VendorTeamPage() {
  const { user, api } = useAuth();
  const [data, setData] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [lastLink, setLastLink] = useState('');
  const [emailStatus, setEmailStatus] = useState('');

  // Founder spec (May 13, 2026 PDF): the producer can drop ONE share
  // link in WhatsApp/email and 5–50 artists join through it. Each row
  // here is a multi-use invite token bound to this producer's id.
  const [teamLinks, setTeamLinks] = useState([]);
  const [linkMaxUses, setLinkMaxUses] = useState(50);
  // Founder ask May 16, 2026 (Vendor Manager PDF): producers can now
  // also invite L2 Vendor Team Managers; managers can only invite L3
  // artists. Default: artist (the common case).
  const [linkRole, setLinkRole] = useState('vendor_artist');
  const [generatingLink, setGeneratingLink] = useState(false);

  const load = () => { api.get('/vendor/my-team').then(r => setData(r.data)).catch(() => {}); };
  const loadTeamLinks = useCallback(() => {
    api.get('/vendor/me/team-invite-links').then(r => setTeamLinks(r.data || [])).catch(() => setTeamLinks([]));
  }, [api]);
  useEffect(() => { load(); loadTeamLinks(); }, [api, loadTeamLinks]);

  const generateTeamLink = async () => {
    setGeneratingLink(true);
    try {
      await api.post('/vendor/me/team-invite-link', {
        max_uses: linkMaxUses,
        role: linkRole,  // Founder May 16: choose between artist (L3) or manager (L2)
      });
      loadTeamLinks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate link');
    }
    setGeneratingLink(false);
  };

  const revokeTeamLink = async (code) => {
    if (!window.confirm('Revoke this invite link? Anyone trying to register with it will be rejected.')) return;
    try {
      await api.post(`/vendor/me/team-invite-links/${code}/revoke`);
      loadTeamLinks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const inviteArtist = async () => {
    if (!email) return;
    setSending(true);
    try {
      const res = await api.post('/vendor/invite-artist', { email, notes });
      setLastLink(res.data.link);
      setEmailStatus(res.data.email_sent ? 'Invite email queued and link copied.' : 'Link copied. Email was not sent by provider.');
      navigator.clipboard.writeText(res.data.link);
      setEmail(''); setNotes('');
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/register/${code}`);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 3000);
  };

  if (!data) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading...</div></div>;

  const depts = data.departments || [];
  const invites = data.invitations || [];
  const artists = data.invited_artists || [];

  return (
    <div className="space-y-5" data-testid="vendor-team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">My Team</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Declared: {data.total_declared} artists | Registered: {data.total_invited} | Tasks: {data.total_tasks} assigned
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="vendor-invite-artist-button" className="h-8 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
          <UserPlus className="w-3.5 h-3.5" /> Invite Artist
        </button>
      </div>

      {lastLink && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-sm px-4 py-2 flex items-center justify-between animate-fade-in" data-testid="vendor-invite-success-banner">
          <div>
            <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {emailStatus || 'Registration link copied!'}</p>
            <p className="text-[9px] font-mono text-zinc-500 mt-0.5">{lastLink}</p>
          </div>
          <button onClick={() => setLastLink('')} data-testid="vendor-invite-success-dismiss"><X className="w-3 h-3 text-zinc-600" /></button>
        </div>
      )}

      {/* My Artists — SHOWN FIRST with role type */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">My Artists ({artists.length})</h3>
          {artists.length === 0 && data.total_declared > 0 && (
            <span className="text-[9px] text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {data.total_declared} artists declared but none registered. Send invite links!</span>
          )}
        </div>
        {artists.length > 0 ? (
          <div className="divide-y divide-zinc-800/30">
            {artists.map(a => {
              const artistType = (a.skills && Array.isArray(a.skills) && a.skills.length > 0) ? a.skills[0] : (a.role_label || a.role || 'Artist');
              return (
                <div key={a.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-zinc-300">{(a.full_name || '?')[0].toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-200 font-semibold">{a.full_name}</p>
                      <p className="text-[10px] text-zinc-500">{a.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-semibold capitalize">{artistType.replace(/_/g, ' ')}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{a.is_active ? 'Active' : 'Inactive'}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${a.nda_status === 'signed' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>NDA: {a.nda_status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-6 text-center">
            <Users className="w-8 h-8 text-zinc-800 mx-auto mb-2" />
            <p className="text-xs text-zinc-600">No artists registered yet</p>
            <p className="text-[10px] text-zinc-700 mt-1">Use "Invite Artist" to send registration links to your team members</p>
          </div>
        )}
      </div>

      {/* DEPARTMENT OVERVIEW — capacity vs workload */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Department Overview — Capacity vs Workload</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="text-left px-4 py-2.5 font-medium">Department</th>
                <th className="text-center px-4 py-2.5 font-medium">Declared</th>
                <th className="text-center px-4 py-2.5 font-medium">Registered</th>
                <th className="text-center px-4 py-2.5 font-medium">Tasks</th>
                <th className="text-center px-4 py-2.5 font-medium">Done</th>
                <th className="text-center px-4 py-2.5 font-medium">Active</th>
                <th className="text-center px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/30">
              {depts.map(d => (
                <tr key={d.key} className="hover:bg-zinc-800/20">
                  <td className="px-4 py-2.5"><span className="text-xs text-zinc-200 font-medium capitalize">{d.label}</span></td>
                  <td className="px-4 py-2.5 text-center"><span className="text-sm font-heading font-bold text-cyan-400">{d.declared_artists}</span></td>
                  <td className="px-4 py-2.5 text-center"><span className={`text-sm font-heading font-bold ${d.active_artists > 0 ? 'text-emerald-400' : d.declared_artists > 0 ? 'text-red-400' : 'text-zinc-600'}`}>{d.active_artists}</span></td>
                  <td className="px-4 py-2.5 text-center text-xs font-mono text-zinc-300">{d.tasks_total}</td>
                  <td className="px-4 py-2.5 text-center text-xs font-mono text-emerald-400">{d.tasks_completed}</td>
                  <td className="px-4 py-2.5 text-center text-xs font-mono text-blue-400">{d.tasks_in_progress}</td>
                  <td className="px-4 py-2.5 text-center">
                    {d.declared_artists > 0 && d.active_artists === 0 ? (
                      <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded-sm">Need artists</span>
                    ) : d.tasks_in_progress > 0 ? (
                      <span className="text-[9px] px-1.5 py-0.5 bg-blue-500/15 text-blue-400 rounded-sm">Working</span>
                    ) : d.tasks_completed === d.tasks_total && d.tasks_total > 0 ? (
                      <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 rounded-sm">Complete</span>
                    ) : (
                      <span className="text-[9px] text-zinc-700">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Share-link Section — multi-use invite link the producer can drop in WhatsApp */}
      <div className="bg-zinc-900 border border-amber-500/20 rounded-sm" data-testid="vendor-team-links-section">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-xs font-semibold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> Share Link · Multi-use
            </h3>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              Drop ONE link in WhatsApp / email — every artist who registers via it becomes a Vendor Team Artist under you. They cannot see your Work Orders, Invoices, or Earnings.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Only L1 producers can invite L2 managers. L2 managers
                always invite L3 artists (founder ask May 16, 2026). */}
            {user?.role === 'vendor_producer' && (
              <label className="text-[10px] text-zinc-500 flex items-center gap-1.5">
                Invite as
                <select
                  value={linkRole}
                  onChange={e => setLinkRole(e.target.value)}
                  data-testid="vendor-team-link-role"
                  className="h-7 bg-zinc-800 border border-zinc-700 rounded text-zinc-200 text-xs px-2"
                >
                  <option value="vendor_artist">Vendor Artist (L3)</option>
                  <option value="vendor_team_manager">Vendor Team Manager (L2)</option>
                </select>
              </label>
            )}
            <label className="text-[10px] text-zinc-500 flex items-center gap-1.5">
              Max uses
              <input
                type="number"
                min="1"
                max="500"
                value={linkMaxUses}
                onChange={e => setLinkMaxUses(parseInt(e.target.value, 10) || 50)}
                data-testid="vendor-team-link-max-uses"
                className="w-16 h-7 bg-zinc-800 border border-zinc-700 rounded text-zinc-200 text-xs px-2"
              />
            </label>
            <button
              onClick={generateTeamLink}
              disabled={generatingLink}
              data-testid="vendor-team-link-generate"
              className="h-7 px-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[10px] font-bold rounded flex items-center gap-1.5 disabled:opacity-50"
            >
              <Link2 className="w-3 h-3" /> {generatingLink ? 'Generating…' : 'Generate Link'}
            </button>
          </div>
        </div>
        <div className="divide-y divide-zinc-800/30">
          {teamLinks.length === 0 && (
            <p className="text-xs text-zinc-600 text-center py-6 italic">
              No share links yet. Generate one to start inviting your team.
            </p>
          )}
          {teamLinks.map(link => {
            const remaining = Math.max(0, (link.max_uses || 0) - (link.uses || 0));
            const expired = remaining === 0;
            return (
              <div key={link.code} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`vendor-team-link-${link.code}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-mono text-zinc-400 truncate" title={link.link}>{link.link}</p>
                  <p className="text-[9px] text-zinc-600 mt-0.5">
                    Used <span className="text-amber-300 font-mono">{link.uses || 0}</span> of <span className="font-mono">{link.max_uses}</span>
                    {' · '}
                    Remaining <span className={`font-mono ${expired ? 'text-red-400' : 'text-emerald-300'}`}>{remaining}</span>
                    {' · '}
                    Created {link.created_at ? new Date(link.created_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => copyLink(link.code)}
                    data-testid={`vendor-team-link-copy-${link.code}`}
                    className={`h-6 px-2 text-[9px] rounded-sm flex items-center gap-1 ${copiedCode === link.code ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}
                  >
                    <Copy className="w-3 h-3" /> {copiedCode === link.code ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={() => revokeTeamLink(link.code)}
                    data-testid={`vendor-team-link-revoke-${link.code}`}
                    className="h-6 px-2 bg-red-500/15 text-red-300 hover:bg-red-500/25 text-[9px] rounded-sm flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Revoke
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sent Invitations */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Sent Invitations ({invites.length})</h3>
        </div>
        <div className="divide-y divide-zinc-800/30">
          {invites.map(inv => (
            <div key={inv.id} className="px-4 py-2.5 flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-300">{inv.artist_email}</p>
                <p className="text-[10px] text-zinc-600">{inv.notes || 'No notes'} | Used: {inv.uses}/{inv.max_uses}</p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => copyLink(inv.code)}
                  className={`h-6 px-2 text-[9px] rounded-sm flex items-center gap-1 ${copiedCode === inv.code ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}>
                  <Copy className="w-3 h-3" /> {copiedCode === inv.code ? 'Copied!' : 'Copy'}
                </button>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${inv.uses > 0 ? 'status-completed' : inv.is_active ? 'status-pending' : 'status-blocked'}`}>
                  {inv.uses > 0 ? 'Registered' : inv.is_active ? 'Pending' : 'Expired'}
                </span>
              </div>
            </div>
          ))}
          {invites.length === 0 && <p className="text-xs text-zinc-600 text-center py-4">No invitations sent yet</p>}
        </div>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-96" data-testid="vendor-invite-modal">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Invite Artist to Your Team</h3>
            <p className="text-[10px] text-zinc-500 mb-3">Artist will receive a registration link with NDA. After signing and registering, they appear in your team and can update tasks.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Artist Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="vendor-invite-email-input"
                    className="w-full h-9 pl-9 pr-3 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="artist@email.com" />
                </div>
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Role / Notes</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} data-testid="vendor-invite-notes-input"
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none"
                  placeholder="e.g., Senior Animator, Lighting Artist" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={inviteArtist} disabled={sending || !email} data-testid="vendor-invite-send-button"
                className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm disabled:opacity-40 flex items-center justify-center gap-2">
                <Send className="w-3.5 h-3.5" /> {sending ? 'Sending...' : 'Generate & Copy Link'}
              </button>
              <button onClick={() => setShowInvite(false)} data-testid="vendor-invite-cancel-button" className="w-20 h-9 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
